import { MenuOutlined } from "@ant-design/icons";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  ConfigProvider,
  Popover,
  Select,
  Table,
  Tooltip,
  Typography,
} from "antd";
import {
  PlusOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  RiseOutlined,
  FallOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { UpdateRankAPI } from "../../API/UpdateRankAPI";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UpdateRankStatus } from "../../API/UpdateRankStatus";
import {
  RANKING_GRID_DEFAULT_WIDTHS,
  rankingTableColumns,
  isSortableColumnKey,
  toApiSortColumnKey,
  getGridHeaderLabel,
} from "../../screens/Rankings/RankingsUtils";
import TableEmptyContent from "../TableEmptyContent/TableEmptyContent";
import { RankingAPI } from "../../API/RankingAPI";
import { useFlightModel } from "../../contexts/FlightModelContext";
import {
  ColumnDragProvider,
  getColumnHeaderCellProps,
  stableColumnHeaderComponents,
  useColumnDrag,
  useRowDrag,
} from "./tableDrag";
import { usePersistentColumnResize } from "./ColumnResize";
const { Text } = Typography;

const DndTable = ({
  listData,
  selectedColumns = [],
  allGridColumnKeys = [],
  setSelectedColumns,
  setOpenAddComments,
  setSelectedID,
  setOpenViewComments,
  getRankingDataFun,
  setSpinning,
  setSelectedRow,
  selectedRow,
  dragableState,
  messageToast,
  selectedRowKeysar,
  setSelectedRowKeys,
  assigneeOptions,
  pageSize,
  page,
  totalRecords,
  filteredValues,
  serachInput,
  setPage,
  setPageSize,
  setTotalRecords,
  setRankingData,
  sortBy,
  sortDir,
  onColumnSort,
  gridPreferencesVersion = 0,
}) => {
   const { selectedFlightModel, refreshFlightModels } = useFlightModel();
   console.log("selectedFlightModel",selectedFlightModel);
  const [dataSource, setDataSource] = useState([]);

  const tableWrapperRef = useRef(null);
  const savedScrollLeftRef = useRef(0);
  const pendingScrollRestoreRef = useRef(false);

  const [statusOptions, setStatusOptions] = useState([]);

  // Prefer the single ranking scrollport (.ant-table-container) so header
  // and body never become separate horizontal scroll targets.
  const getTableScrollTargets = useCallback(() => {
    const root = tableWrapperRef.current;
    if (!root) return [];

    const primary = root.querySelector(".ant-table-container");
    if (primary) return [primary];

    const fallback = root.querySelector(".ant-table-content");
    return fallback ? [fallback] : [];
  }, []);

  const saveScrollPosition = useCallback(() => {
    const [scroller] = getTableScrollTargets();
    if (!scroller) return;
    savedScrollLeftRef.current = scroller.scrollLeft || 0;
  }, [getTableScrollTargets]);

  const handleColumnReorder = useCallback(() => {
    saveScrollPosition();
    pendingScrollRestoreRef.current = true;
  }, [saveScrollPosition]);

  useEffect(() => {
    RankingAPI.getStatusOptions()
      .then((res) => {
        if (res?.data) {
          const options = res?.data?.statuses?.map((item) => ({
            value: item.name,
            label: item.name,
          }));
          setStatusOptions([{ value: null, label: "--select--" }, ...options]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Row keys must identify the record, not its position on the page.
    // Positional keys ("1", "2", "3"...) restart on every page, so with
    // preserveSelectedRowKeys the selection made on page 1 was inherited by
    // whichever rows happened to sit in those slots on the next page.
    let arr = listData?.map((ele, index) => {
      ele.key = String(
        ele.id ?? ele.ranked_id ?? ele.spec_concat ?? `row-${index + 1}`,
      );
      return ele;
    });
    setDataSource(arr);
  }, [listData]);

  const buildSelectedRowPayload = (record, keys) => ({
    id: record?.id,
    key: keys,
    spec: record?.spec_concat,
    rank: record?.rank,
    repeat_val: record?.repeat_val,
    repeat_max: record?.repeat_max,
    initial_max: record?.initial_max,
    total_labor_cost_max: record?.total_labor_cost_max,
    interval_type: record?.interval_type,
    total_effort_hours: record?.total_effort_hours,
    yield_percentage: record?.yield_percentage,
  });

  const selectRowFun = useCallback(
    (record) => {
      setSelectedRowKeys((prevKeys) => {
        const alreadySelected = prevKeys.includes(record.key);
        const nextKeys = alreadySelected
          ? prevKeys.filter((key) => key !== record.key)
          : [...prevKeys, record.key];

        if (nextKeys.length === 0) {
          localStorage.setItem("selectedRow", "");
          setSelectedRow({});
          return nextKeys;
        }

        const primaryKey = nextKeys[nextKeys.length - 1];
        const primaryRecord =
          (alreadySelected
            ? dataSource.find((row) => row.key === primaryKey)
            : record) || record;

        localStorage.setItem("selectedRow", primaryKey);
        setSelectedRow(buildSelectedRowPayload(primaryRecord, nextKeys));
        return nextKeys;
      });
    },
    [dataSource, setSelectedRow, setSelectedRowKeys],
  );

  const columns = useMemo(() => {
    const renderSortableTitle = (label, columnKey) => {
      const isActiveColumn = sortBy === columnKey;
      const sortHint = isActiveColumn
        ? sortDir === "asc"
          ? "Sorted ascending — click to sort descending"
          : sortDir === "desc"
          ? "Sorted descending — click to clear sort"
          : "Click to sort"
        : "Click to sort";

      // Sorting is triggered from the label/indicator only, so dragging,
      // resizing or clicking anywhere else in the header cell never sorts.
      const triggerSort = (event) => {
        event.stopPropagation();
        onColumnSort?.(columnKey);
      };

      return (
        <Popover
          title=""
          content={
            <p
              style={{
                fontWeight: 500,
                color: "#565656",
                fontSize: "15px",
                lineHeight: "1.5rem",
                margin: 0,
              }}
            >
              {sortHint}
            </p>
          }
        >
          <span
            className="ranking-column-header-title ranking-column-header-title--sortable"
            role="button"
            tabIndex={0}
            aria-label={`${typeof label === "string" ? label : columnKey}: ${sortHint}`}
            onClick={triggerSort}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                triggerSort(event);
              }
            }}
          >
            <span className="ranking-column-header-label">{label}</span>
            {isActiveColumn && (sortDir === "asc" || sortDir === "desc") && (
              <span className="ranking-column-sort-indicator" aria-hidden="true">
                {sortDir === "asc" ? (
                  <CaretUpOutlined className="active" />
                ) : (
                  <CaretDownOutlined className="active" />
                )}
              </span>
            )}
          </span>
        </Popover>
      );
    };

    const withSorting = (column) => {
      const columnKey = column?.key;
      if (!isSortableColumnKey(columnKey)) {
        return column;
      }

      return {
        ...column,
        title: renderSortableTitle(column.title, columnKey),
      };
    };

    const sortColumn = [...selectedColumns]
      .sort((a, b) => a.rank - b.rank)
      .map((col) => {
        const columnStyle = rankingTableColumns.find((c) => c.key === col.id);
        return withSorting({
          ...columnStyle,
          title: getGridHeaderLabel(col.id, col.label),
          dataIndex: col.id,
          key: col.id,
          onHeaderCell: () => getColumnHeaderCellProps(col.id),
          onCell: (record) => ({
            onClick: () => {
              selectRowFun(record);
            },
          }),
        });
      });

    // Status, Comments and Assigned To are part of selectedColumns now, so they
    // render (in their configured order / visibility) through sortColumn above
    // instead of being appended here as fixed trailing columns.
    return [
      {
        key: "sort",
        width: 40,
      },
      ...sortColumn,
    ];
  }, [selectedColumns, selectRowFun, sortBy, sortDir, onColumnSort]);

  const allRankingGridColumnKeys = useMemo(() => {
    if (allGridColumnKeys.length > 0) {
      return allGridColumnKeys;
    }

    return selectedColumns.map((column) => column.id).filter(Boolean);
  }, [allGridColumnKeys, selectedColumns]);

  const defaultColumnWidthMap = useMemo(() => {
    const widths = rankingTableColumns.reduce((accumulator, column) => {
      const key = column?.key;
      if (!key || key === "sort") {
        return accumulator;
      }

      const width =
        typeof column?.width === "number"
          ? column.width
          : Number.parseInt(column?.width, 10);
      if (Number.isFinite(width)) {
        accumulator[key] = width;
      }
      return accumulator;
    }, {});

    return { ...RANKING_GRID_DEFAULT_WIDTHS, ...widths };
  }, []);

  const {
    columnsWithResize,
    hasFrozenColumns,
    isColumnFrozen,
    preferencesReady,
  } = usePersistentColumnResize({
    columns,
    allColumnKeys: allRankingGridColumnKeys,
    defaultWidthMap: defaultColumnWidthMap,
    minWidth: 90,
    messageToast,
    sortPreferences: {
      sort_by: toApiSortColumnKey(sortBy),
      sort_dir: sortDir,
    },
    gridPreferencesVersion,
  });

  // Align sticky left offsets to real rendered column widths so header and
  // body stay in sync (avoids freeze-edge shadow cutting through body cells).
  useLayoutEffect(() => {
    if (!hasFrozenColumns) return undefined;

    const root = tableWrapperRef.current;
    if (!root) return undefined;

    const syncStickyColumnOffsets = () => {
      const headerRow = root.querySelector(".ant-table-thead > tr");
      if (!headerRow) return;

      const headerStickyCells = Array.from(
        headerRow.querySelectorAll(
          ":scope > .ant-table-selection-column, :scope > .ranking-frozen-cell",
        ),
      );
      if (!headerStickyCells.length) return;

      // Prefer layout widths (offset*) over getBoundingClientRect so sticky
      // visual position after horizontal scroll does not skew measurements.
      let nextLeft = 0;
      const measured = headerStickyCells.map((cell) => {
        const width = cell.offsetWidth;
        const left = nextLeft;
        nextLeft += width;
        return { left, width };
      });

      // Only the sticky offset is written back. Writing width/min-width here
      // would pin the frozen cells to their measured size, and because this
      // effect re-runs (and the ResizeObserver refires) on every resize frame
      // it would re-measure its own pinned value — leaving frozen columns
      // unable to shrink. Column width stays owned by the colgroup.
      const applyMeasured = (cell, index) => {
        const meta = measured[index];
        if (!meta) return;
        cell.style.setProperty("left", `${meta.left}px`, "important");
        cell.style.removeProperty("width");
        cell.style.removeProperty("min-width");
      };

      headerStickyCells.forEach(applyMeasured);

      root.querySelectorAll(".ant-table-tbody > tr").forEach((row) => {
        const bodyStickyCells = Array.from(
          row.querySelectorAll(
            ":scope > .ant-table-selection-column, :scope > .ranking-frozen-cell",
          ),
        );
        bodyStickyCells.forEach(applyMeasured);
      });
    };

    syncStickyColumnOffsets();
    const rafId = requestAnimationFrame(syncStickyColumnOffsets);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => syncStickyColumnOffsets())
        : null;
    const tableEl = root.querySelector(".ant-table");
    if (resizeObserver && tableEl) {
      resizeObserver.observe(tableEl);
    }

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
    };
  }, [hasFrozenColumns, columnsWithResize, dataSource, page, pageSize]);

  useEffect(() => {
    const scrollTargets = getTableScrollTargets();
    if (!scrollTargets.length) return;

    const handleScroll = (event) => {
      if (!pendingScrollRestoreRef.current) {
        savedScrollLeftRef.current = event.target.scrollLeft;
      }
    };

    scrollTargets.forEach((target) => {
      target.addEventListener("scroll", handleScroll, { passive: true });
    });

    return () => {
      scrollTargets.forEach((target) => {
        target.removeEventListener("scroll", handleScroll);
      });
    };
  }, [columns, dataSource, getTableScrollTargets]);

  useLayoutEffect(() => {
    if (!pendingScrollRestoreRef.current) return;

    const scrollLeft = savedScrollLeftRef.current;
    const apply = () => {
      getTableScrollTargets().forEach((target) => {
        target.scrollLeft = scrollLeft;
      });
    };

    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(() => {
        apply();
        pendingScrollRestoreRef.current = false;
      });
    });
  }, [columns, getTableScrollTargets]);

  const { contextValue: columnDragContextValue } = useColumnDrag({
    selectedColumns,
    setSelectedColumns,
    messageToast,
    onColumnDragStart: saveScrollPosition,
    onColumnReorder: handleColumnReorder,
    isColumnFrozen,
  });

  const canManualRerank = dragableState === true && sortBy === "rank";
  const manualRerankDisabledHint =
    dragableState !== true
      ? "To change the rank, you must clear all filters applied"
      : "Manual re-ranking is available only when the table is sorted by Rank";

  const onDragEnd = async ({ active, over }) => {
    if (!canManualRerank) {
      messageToast("warning", manualRerankDisabledHint);
      return;
    }

    if (over?.id && active.id !== over?.id) {
      const activeInd = dataSource.findIndex((i) => i.key === active.id);
      const overInd = dataSource.findIndex((i) => i.key === over?.id);

      let arr = [];
      if (activeInd > overInd) {
        for (let index = overInd; index < activeInd; index++) {
          const element = dataSource[index]?.spec_concat;
          arr.push(element);
        }
      } else {
        for (let index = activeInd + 1; index <= overInd; index++) {
          const element = dataSource[index]?.spec_concat;
          arr.push(element);
        }
      }

      if (dataSource[activeInd]?.rank !== dataSource[overInd]?.rank) {
        let flightModelToUse = selectedFlightModel;

        // Fallback: fetch from context if not available
        if (!flightModelToUse) {
          const refreshed = await refreshFlightModels();
          flightModelToUse = refreshed?.selectedFlightModel || null;
        }
        if (!flightModelToUse) {
          messageToast("error", "No flight model selected.");
          setSpinning(false);
          return;
        }
        const bodyData = {
          flight_model: flightModelToUse,
          //  localStorage.getItem("flight_model"),

          old_rank: dataSource[activeInd]?.rank,
          new_rank: dataSource[overInd]?.rank,
          spec_list: arr,
          selected_spec: dataSource[activeInd]?.spec_concat,
        };

        setDataSource((previous) => {
          const activeIndex = previous.findIndex((i) => i.key === active.id);
          const overIndex = previous.findIndex((i) => i.key === over?.id);
          return arrayMove(previous, activeIndex, overIndex);
        });

        updateRankFun(bodyData);
      }
    }
  };

  async function updateRankFun(bodyData) {
    await UpdateRankAPI.dragandupdateRank(bodyData)
      .then((res) => {
        if (res.data?.status === true) {
          setSelectedRowKeys([]);
          setSelectedRow({});
          getRankingDataFun(page, pageSize, false);
          messageToast("success", res.data?.message);
        } else {
          messageToast("error", res.data?.message);
          if (res.data?.reload === true) {
            setTimeout(() => {
              window.location.reload();
            }, "2000");
          }
        }
      })
      .catch((err) => {
        messageToast("error", "Internal Server Error");
      });
  }

  const Row = ({ children, ...props }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: props["data-row-key"],
      disabled: !canManualRerank,
    });
    const isSelected = selectedRowKeysar.includes(props['data-row-key'])
    const style = {
      ...props.style,
      transform: CSS.Transform.toString(
        transform && {
          ...transform,
          scaleY: 1,
        },
      ),
      transition,
      ...(isDragging
        ? {
            position: "relative",
            zIndex: 9999,
          }
        : {}),
    };
    return (
      <tr {...props} ref={setNodeRef} style={{ ...style }} {...attributes}>
        {React.Children.map(children, (child) => {
          let strDate = "";
          if (child.props?.record?.assigned_on) {
            strDate = new Date(child.props?.record?.assigned_on);
          }

          if (child.key === "sort") {
            const dragHandle = (
              <MenuOutlined
                ref={canManualRerank ? setActivatorNodeRef : undefined}
                style={{
                  touchAction: "none",
                  cursor: canManualRerank ? "move" : "not-allowed",
                  opacity: canManualRerank ? 1 : 0.35,
                  color: canManualRerank ? undefined : "#bfbfbf",
                }}
                {...(canManualRerank ? listeners : {})}
              />
            );

            return React.cloneElement(child, {
              children: canManualRerank ? (
                dragHandle
              ) : (
                <Popover
                  title=""
                  content={
                    <p
                      style={{
                        fontWeight: 500,
                        color: "#565656",
                        fontSize: "15px",
                        lineHeight: "1.5rem",
                        margin: 0,
                      }}
                    >
                      {manualRerankDisabledHint}
                    </p>
                  }
                >
                  <span style={{ display: "inline-flex" }}>{dragHandle}</span>
                </Popover>
              ),
            });
          }
          if (child.key === "spec_concat") {
            return React.cloneElement(child, {
              children: (
                <div>
                  <Tooltip title={child.props.record?.task_description}>
                    <span style={{ cursor: "pointer" }}>
                      {child.props.record?.spec_concat}
                    </span>
                  </Tooltip>
                </div>
              ),
            });
          }
          if (child.key === "card_no") {
            return React.cloneElement(child, {
              children: (
                <div>
                  <Popover
                    content={
                      <>
                        <div
                          style={{
                            display: "flex",
                            fontWeight: 500,
                            color: "#565656",
                            fontSize: "15px",
                            lineHeight: "1.5rem",
                          }}
                        >
                          <p style={{ width: "130px" }}>Custom Initial</p>
                          <p>: {child.props.record?.custom_initial}</p>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            fontWeight: 500,
                            color: "#565656",
                            fontSize: "15px",
                            lineHeight: "1.5rem",
                          }}
                        >
                          <p style={{ width: "130px" }}>Custom Repeat</p>
                          <p>: {child.props.record?.custom_repeat}</p>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            fontWeight: 500,
                            color: "#565656",
                            fontSize: "15px",
                            lineHeight: "1.5rem",
                          }}
                        >
                          <p style={{ width: "130px" }}>MRBR Initial</p>
                          <p>: {child.props.record?.mrbr_initial}</p>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            fontWeight: 500,
                            color: "#565656",
                            fontSize: "15px",
                            lineHeight: "1.5rem",
                          }}
                        >
                          <p style={{ width: "130px" }}>MRBR Repeat</p>
                          <p>: {child.props.record?.mrbr_repeat}</p>
                        </div>
                      </>
                    }
                    title={""}
                  >
                    <span style={{ cursor: "pointer", color: isSelected ? 'white' : '#1579ed' }}>
                      {child.props.record?.card_no}
                    </span>
                  </Popover>
                </div>
              ),
            });
          }
          if (child.key === "comments") {
            return React.cloneElement(child, {
              children:
                child.props.record.have_comments === false ? (
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: "#EBEEF8",
                      },
                    }}
                  >
                    <Button
                      onClick={(e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        openCommentFun(child.props.record.id);
                      }}
                      type="primary"
                      style={{ color: "#304CB2" }}
                      icon={<PlusOutlined />}
                    >
                      Add
                    </Button>
                  </ConfigProvider>
                ) : (
                  <ConfigProvider
                    theme={{
                      token: {
                        colorPrimary: "#304CB2",
                      },
                    }}
                  >
                    <Button
                      onClick={(e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        viewCommentFun(child.props.record.id);
                      }}
                      type="primary"
                      style={{}}
                      icon={<EyeOutlined />}
                    >
                      View
                    </Button>
                  </ConfigProvider>
                ),
            });
          }
          if (child.key === "status") {
            return React.cloneElement(child, {
              children: (
                <Select
                  defaultValue={child.props.record.status}
                  style={{
                    width: 140,
                    fontSize: "13px",
                  }}
                  value={child.props.record.status}
                  onChange={(e) => statusOnchangeFun(e, child.props.record.id)}
                  options={statusOptions}
                  onClick={(e) => {
                    if (e && e.stopPropagation) e.stopPropagation();
                  }}
                />
              ),
            });
          }
          if (child.key === "wpm_score") {
            return React.cloneElement(child, {
              children: (
                <div>
                  {child.props.record?.is_drag === true ? (
                    <Tooltip title="Manually Moved">
                      <span style={{ color: "red", fontWeight: 500 }}>
                        {child.props.record?.wpm_score.toFixed(3)}
                      </span>
                    </Tooltip>
                  ) : (
                    <span>{child.props.record?.wpm_score.toFixed(3)}</span>
                  )}
                </div>
              ),
            });
          }
          if (child.key === "rank") {
            return React.cloneElement(child, {
              children: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {child.props.record.rank}
                  {child.props.record.graph_dir === "down" ? (
                    <FallOutlined style={{ marginLeft: "5px", color: "red" }} />
                  ) : child.props.record.graph_dir === "up" ? (
                    <RiseOutlined
                      style={{ marginLeft: "5px", color: "green" }}
                    />
                  ) : (
                    <span
                      style={{
                        display: "inline-block",
                        width: "12px",
                        height: "2px",
                        borderRadius: "2px",
                        backgroundColor: "#526ebf",
                        marginLeft: "5px",
                      }}
                    ></span>
                  )}
                </div>
              ),
            });
          }
          if (child.key === "assigned_to") {
            return React.cloneElement(child, {
              children: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                  }}
                >
                  {child.props.record.assigned_to ? (
                    <>
                      <Select
                        defaultValue={child.props.record.assigned_to}
                        style={{
                          width: 100,
                          fontSize: "13px",
                          marginRight: "5px",
                        }}
                        value={child.props.record.assigned_to}
                        onChange={(e) =>
                          assignOnchangeFun(e, child.props.record.id)
                        }
                        options={assigneeOptions}
                        onClick={(e) => {
                          if (e && e.stopPropagation) e.stopPropagation();
                        }}
                      />
                      {child.props.record.assigned_to && (
                        <Text
                          style={{
                            fontSize: "12px",
                            textTransform: "capitalize",
                            color: "#000",
                          }}
                        >
                          {" "}
                          - on {String(strDate).slice(4, 15)}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Select
                      defaultValue={child.props.record.assigned_to}
                      style={{
                        width: "100%",
                        fontSize: "13px",
                        marginRight: "5px",
                      }}
                      value={child.props.record.assigned_to}
                      onChange={(e) =>
                        assignOnchangeFun(e, child.props.record.id)
                      }
                      options={assigneeOptions}
                      onClick={(e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                      }}
                    />
                  )}
                </div>
              ),
            });
          }
          return child;
        })}
      </tr>
    );
  };

  /*
   * `Row` above is re-created on every render, so it is a new component *type*
   * each time and React cannot reconcile the rows — it unmounts and remounts
   * every <tr>. On each checkbox click that emptied and rebuilt the whole
   * <tbody> inside .ant-table-container (a `max-height: 740px; overflow: auto`
   * scrollport), the browser clamped its scrollTop, and the grid visibly
   * deflected up/down. It also discarded the sticky `left` offsets written
   * imperatively onto the frozen cells.
   *
   * StableRow is created once and delegates to the current render's `Row`, so
   * rows now update in place. Rendering logic is unchanged.
   */
  const rowRenderRef = useRef(Row);
  rowRenderRef.current = Row;
  const [StableRow] = useState(
    () => (rowProps) => rowRenderRef.current(rowProps),
  );

  async function statusOnchangeFun(e, id) {
       let flightModelToUse = selectedFlightModel;

    // Fallback: fetch from context if not available
    if (!flightModelToUse) {
      const refreshed =await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }
    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");setSpinning(false);
      return;
    }
    if (e !== null) {
      setSpinning(true);
      let arr = [...dataSource];
      arr.map((ele) => {
        if (ele.id === id) {
          ele.status = e;
        }
        return ele;
      });

      const bodyData = {
        id: id,
        status: e,
        flight_model: flightModelToUse,
        // localStorage.getItem("flight_model"),
      };

      await UpdateRankStatus.updateStatus(bodyData)
        .then((res) => {
          if (res.data?.status === true) {
            //setDataSource(arr)
            messageToast("success", res.data?.message);
            if (["rejected", "completed"].includes(String(e).toLowerCase())) {
              const rowKey = dataSource.find((item) => item.id === id)?.key;
              if (rowKey) {
                setSelectedRowKeys((prev) =>
                  prev.filter((key) => String(key) !== String(rowKey)),
                );
                setSelectedRow((prev) =>
                  String(prev?.id) === String(id) ? {} : prev,
                );
              }
            }
            getRankingDataFun(page, pageSize, false);
          } else {
            messageToast("error", res.data?.message);
            setSpinning(false);
            if (res.data?.reload === true) {
              setTimeout(() => {
                window.location.reload();
              }, "2000");
            }
          }
        })
        .catch((err) => {
          messageToast("error", "Internal Server Error");
          setSpinning(false);
        });
      // .finally(() => setSpinning(false))
    } else {
      messageToast("warning", "Status cannot be assigned a null value");
    }
  }

  async function assignOnchangeFun(e, id) {
       let flightModelToUse = selectedFlightModel;

    // Fallback: fetch from context if not available
    if (!flightModelToUse) {
      const refreshed =await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }
    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");setSpinning(false);
      return;
    }
    if (e !== "") {
      setSpinning(true);
      let arr = [...dataSource];
      arr.map((ele) => {
        if (ele.id === id) {
          ele.assigned_to = e;
        }
        return ele;
      });

      const bodyData = {
        id: id,
        asg: e,
        flight_model: flightModelToUse
        // localStorage.getItem("flight_model"),
      };

      await UpdateRankStatus.updateAssignee(bodyData)
        .then((res) => {
          if (res.data?.status === true) {
            //setDataSource(arr)
            messageToast("success", res.data?.message);
            getRankingDataFun(page, pageSize, false);
          } else {
            messageToast("error", res.data?.message);
            setSpinning(false);
          }
        })
        .catch((err) => {
          messageToast("error", "Internal Server Error");
          setSpinning(false);
        });
      // .finally(() => setSpinning(false))
    } else {
      messageToast("warning", "The Assignee cannot be assigned a null value");
    }
  }

  function openCommentFun(id) {
    setSelectedID(id);
    setOpenAddComments(true);
  }

  function viewCommentFun(id) {
    setSelectedID(id);
    setOpenViewComments(true);
  }

  const checkHasFilter = () => {
    return (
      filteredValues?.cat?.trim() !== "" ||
      filteredValues?.specType?.trim() !== "" ||
      filteredValues?.status?.length > 0 ||
      filteredValues?.task?.length > 0 ||
      filteredValues?.yieldValueFrom !== 0 ||
      filteredValues?.yieldValueTo !== 100 ||
      filteredValues?.maintenanceCheck?.trim() !== "" ||
      filteredValues?.maintenanceCheckPackage?.trim() !== "" ||
      filteredValues?.maintenanceCheckInterval?.trim() !== "" ||
      filteredValues?.maintenanceCheckPackageInterval?.trim() !== "" ||
      serachInput.trim() !== ""
    );
  };
  //      const handleTableChange = (pagination) => {
  //   const { current, pageSize } = pagination;

  //   getRankingDataFun(current, pageSize);
  // };
  const handleTableChange = (pagination, filters, sorter, extra) => {
    if (extra?.action === "sort") {
      return;
    }

    const { current, pageSize: nextPageSize } = pagination;

    getRankingDataFun(current, nextPageSize, false, {
      sortBy,
      sortDir,
    });

    setPage(current);
    setPageSize(nextPageSize);
  };

  const rowKeys = useMemo(
    () => dataSource.map((row) => row.key),
    [dataSource],
  );

  const { BodyWrapper } = useRowDrag({
    rowKeys,
    onDragEnd,
  });

  const mappedSelectedRowKeys = useMemo(() => {
    return (selectedRowKeysar || []).map((key) => {
      const match = dataSource.find(
        (row) =>
          String(row.id) === String(key) ||
          String(row.ranked_id) === String(key),
      );
      return match ? match.key : key;
    });
  }, [selectedRowKeysar, dataSource]);

  return (
    <ColumnDragProvider value={columnDragContextValue}>
    <div ref={tableWrapperRef}>
    {/* First ever load has no cached layout to seed from, so hold the grid
        (under the page spinner) until the saved widths land instead of
        painting the default widths and snapping to the real ones. */}
    {preferencesReady && (
    <Table
      // Do not use Ant Design sticky / scroll.y — they split header & body.
      // Sticky header is CSS-only inside .ant-table-container (table scroll).
      scroll={{ x: "max-content" }}
      components={{
        header: stableColumnHeaderComponents,
        body: {
          wrapper: BodyWrapper,
          row: StableRow,
        },
      }}
      locale={{
        emptyText: <TableEmptyContent />,
      }}
      rowSelection={{
        // type: "radio",
        type: "checkbox",
        columnWidth: 48,
        selectedRowKeys: mappedSelectedRowKeys,
        preserveSelectedRowKeys: true,
        onChange: (selectedRowKeys, selectedRows) => {
          const primary = selectedRows[selectedRows.length - 1];
          localStorage.setItem(
            "selectedRow",
            selectedRowKeys[selectedRowKeys.length - 1] ?? "",
          );
          setSelectedRowKeys(selectedRowKeys);
          if (!primary) {
            setSelectedRow({});
            return;
          }
          setSelectedRow(buildSelectedRowPayload(primary, selectedRowKeys));
        },
      }}
      rowKey="key"
      columns={columnsWithResize}
      dataSource={dataSource}
      className={`antdTable ranking-table${hasFrozenColumns ? " ranking-table--has-frozen" : ""}`}
      pagination={{
        current: page,
        pageSize: pageSize,
        total: totalRecords,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
      }}
      onChange={handleTableChange}
    />
    )}
    </div>
    </ColumnDragProvider>
  );
};
export default DndTable;
